import React, { useState, useRef } from 'react';
import { Button } from 'ui';
import DescriptorComponent from './DescriptorComponent';
import { Scrollbars } from 'react-custom-scrollbars';
import GenericOnboardingMessage from './GenericOnboardingMessage';
import { 
  useCreateDescriptor, 
  useUpdateDescriptor, 
  useDeleteDescriptor,
  useCreateSelection,
  useAddAndSelectChoice 
} from 'hooks/descriptors/mutations';

const EditDemographicView = ({ projectID, transcriptID, descriptors }) => {
  const [createDescriptor, setCreateDescriptor] = useState(false);
  const [saving, setSaving] = useState(false);
  const messagesEndRef = useRef(null);

  // Initialize React Query mutation hooks
  const createDescriptorMutation = useCreateDescriptor();
  const updateDescriptorMutation = useUpdateDescriptor();
  const deleteDescriptorMutation = useDeleteDescriptor();
  const createSelectionMutation = useCreateSelection();
  const addAndSelectChoiceMutation = useAddAndSelectChoice();

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const addAndSelectChoice = (descriptorID, choiceText) => {
    if (transcriptID) {
      addAndSelectChoiceMutation.mutate({ 
        transcriptID, 
        descriptorID, 
        choiceText,
        projectID 
      });
    }
  };

  const createSelection = (choiceID) => {
    if (transcriptID) {
      createSelectionMutation.mutate({ transcriptID, choiceID });
    }
  };

  const updateDescriptor = (id, name, newPayload, updatePayload, deletePayload) => {
    // Prepare the choices array, combining all payloads
    const choices = [
      // New choices (no ID)
      ...newPayload.map(choice => ({ 
        choice: choice.choice 
      })),
      
      // Updated existing choices (with IDs)
      ...updatePayload.map(choice => ({ 
        id: choice.id, 
        choice: choice.choice 
      })),
      
      // Deleted choices (marked with _destroy flag)
      ...deletePayload.map(choice => ({ 
        id: choice.id, 
        _destroy: true 
      }))
    ];
    
    updateDescriptorMutation.mutate({ 
      id, 
      name, 
      choices,
      projectID
    });
  };

  const handleCreateDescriptor = (id, name, newPayload, updatePayload, deletePayload) => {
    if (projectID) {
      setSaving(true);
      
      // For new descriptors, we only need the new choices
      const choices = newPayload.map(choice => ({ 
        choice: choice.choice 
      }));
      
      createDescriptorMutation.mutate(
        { 
          projectID, 
          name, 
          choices
        },
        {
          onSuccess: () => {
            setCreateDescriptor(false);
            setSaving(false);
          },
          onError: () => {
            setSaving(false);
          }
        }
      );
    }
  };

  const handleDeleteDescriptor = (descriptorID) => {
    if (descriptorID && projectID) {
      deleteDescriptorMutation.mutate({ id: descriptorID, projectID });
    }
    setCreateDescriptor(false);
  };

  const handleAddDescriptor = () => {
    setCreateDescriptor(true);
    scrollToBottom();
  };

  const descriptorElements = descriptors.map((descriptor) => (
    <DescriptorComponent
      doneEditingCallback={updateDescriptor}
      key={descriptor.id}
      descriptor={descriptor}
      deleteCallback={handleDeleteDescriptor}
      createSelectionCallback={createSelection}
      addAndSelectChoiceCallback={addAndSelectChoice}
    />
  ));

  const renderContent = () => (
    <>
      <p>
        Add demographics and descriptors to the transcript in order to filter by it later.
      </p>
      {descriptorElements}

      {createDescriptor && (
        <DescriptorComponent
          descriptor={null}
          doneEditingCallback={handleCreateDescriptor}
          deleteCallback={handleDeleteDescriptor}
          addAndSelectChoiceCallback={addAndSelectChoice}
          disabled={saving}
        />
      )}

      <div style={{height:"200px", width:"10px"}}/>
      <div
        style={{ float:"left", clear: "both" }}
        ref={messagesEndRef}
      />
    </>
  );

  return (
    <div style={{padding:"10px", height:"100%"}}>
      <Scrollbars autoHide style={{height:"100%"}}>
        {(!descriptors.length && !createDescriptor) ? (
          <GenericOnboardingMessage
            header="Cut and splice data by descriptors"
            body="Assign descriptors in order to filter by them later."
            buttonText="Add A Descriptor"
            onClick={() => setCreateDescriptor(true)}
            intercomArticleId={process.env['INTERCOM_ARITCLE_DESCRIPTORS']}
            helpArticleText={'Learn more about descriptors'}
          />
        ) : renderContent()}
      </Scrollbars>

      {(descriptors.length > 0 || createDescriptor) && (
        <div style={{
          position: "absolute",
          left: "20px",
          bottom: "40px",
          zIndex: 10,
        }}>
          <Button
            style={{
              position: "relative",
              width: "280px"
            }}
            variant="blue"
            onClick={handleAddDescriptor}
            disabled={createDescriptor}
          >
            <span style={{margin:"10px 50px"}}>Add Descriptor</span>
          </Button>
        </div>
      )}
    </div>
  );
};

export default EditDemographicView;
